import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, CheckCircle, X, RotateCcw, Trash2, Eye, Clock } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { formatDateTime } from '../../utils/timeUtils';
import OrderList from '../../components/OrderList';
import type { OrderFilterExt } from '../../hooks/useOrders';
import AuthConfirmModal from '../../components/AuthConfirmModal';

export default function StudentOrders() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [durationFilter, setDurationFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // 弹窗状态
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState(false);
  const [showRefundDetailModal, setShowRefundDetailModal] = useState(false);
  const [refundForm, setRefundForm] = useState({ amount: 0, destination: 'original' as 'original' | 'balance', reason: '' });
  const [pendingBatchIds, setPendingBatchIds] = useState<string[]>([]);

  // 统计数据（独立查询）
  const [stats, setStats] = useState({ total: 0, pending: 0, paid: 0, refunded: 0, revenue: 0 });
  const [serverIp, setServerIp] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  // 获取 Edge Function 出口 IP
  const fetchServerIp = async () => {
    try {
      const res = await fetch(`${getSupabaseUrl()}/functions/v1/alipay-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getIp' }),
      });
      const data = await res.json();
      if (data.success) {
        setServerIp(data.ip);
        alert(`服务器出口IP: ${data.ip}\n请将此IP添加到支付宝开放平台的应用IP白名单中`);
      } else {
        alert('获取IP失败: ' + data.error);
      }
    } catch (e) {
      alert('获取IP失败');
    }
  };

  const loadStats = async () => {
    const { data } = await supabase
      .from('orders')
      .select('status, actual_price, price')
      .eq('role', 'student');

    const orders = data || [];
    setStats({
      total: orders.length,
      pending: orders.filter(o => o.status === 'pending').length,
      paid: orders.filter(o => o.status === 'paid').length,
      refunded: orders.filter(o => o.status === 'refunded').length,
      revenue: orders.filter(o => o.status === 'paid').reduce((sum, o) => sum + (o.actual_price != null ? Number(o.actual_price) : Number(o.price) || 0), 0),
    });
  };

  const filterExt: OrderFilterExt = useMemo(() => ({
    searchQuery,
    status: statusFilter === 'all' ? undefined : statusFilter,
    duration: durationFilter === 'all' ? undefined : durationFilter,
    source: sourceFilter === 'all' ? undefined : sourceFilter,
    dateRange: dateRange.start || dateRange.end ? { start: dateRange.start, end: dateRange.end } : undefined,
  }), [searchQuery, statusFilter, durationFilter, sourceFilter, dateRange]);

  // 退款操作
  const openRefundModal = (order: any) => {
    setSelectedOrder(order);
    setRefundForm({
      amount: order.actualPrice != null ? Number(order.actualPrice) : Number(order.price),
      destination: 'original',
      reason: '',
    });
    setShowRefundModal(true);
  };

  const handleRefundSubmit = async () => {
    if (!selectedOrder) return;

    const session = (await supabase.auth.getSession()).data.session;

    // 幂等性检查：先查询订单当前状态，防止重复退款
    const { data: currentOrder } = await supabase
      .from('orders')
      .select('status, refund_status')
      .eq('id', selectedOrder.id)
      .maybeSingle();

    if (currentOrder?.status === 'refunded' || currentOrder?.refund_status === 'refunded') {
      alert('该订单已退款，请勿重复操作');
      setShowRefundModal(false);
      setSelectedOrder(null);
      return;
    }

    // 判断是否为无实际支付的订单（后台开通/券码兑换）
    const isNoPaymentOrder = selectedOrder.payMethod === 'admin' || selectedOrder.payMethod === 'coupon';

    if (!isNoPaymentOrder) {
      // 1. 处理学员U币抵扣退回
      if (selectedOrder.pointsDeducted && selectedOrder.pointsDeducted > 0) {
        try {
          const pointsRefundRes = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
              action: 'add_points',
              user_id: selectedOrder.userId,
              role: 'student',
              points: selectedOrder.pointsDeducted,
              point_type: 'activity_reward',
              source_type: 'refund',
              source_id: selectedOrder.id,
              source_detail: { order_no: selectedOrder.orderNo, original_deduction: selectedOrder.pointsDeducted },
              remark: `订单退款退回U币: 订单${selectedOrder.orderNo}, 退回${selectedOrder.pointsDeducted}U币`,
            }),
          });
          const pointsRefundData = await pointsRefundRes.json();
          if (!pointsRefundData.success) {
            alert('U币退回失败: ' + (pointsRefundData.error || '未知错误'));
            return;
          }
        } catch (e) {
          alert('U币退回接口调用失败');
          return;
        }
      }

      // 2. 回滚教师返利U币（学员购课时绑定的教师获得返利，学员自己没有返利）
      // 教师返利记录: point_type='activity_reward', source_type='teacher_rebate', source_id=orderId
      try {
        const { data: teacherRebateTxs } = await supabase
          .from('point_transactions')
          .select('id, user_id, role, points_change, point_type')
          .eq('source_id', selectedOrder.id)
          .eq('source_type', 'teacher_rebate')
          .eq('role', 'teacher')
          .gt('points_change', 0);

        if (teacherRebateTxs && teacherRebateTxs.length > 0) {
          for (const tx of teacherRebateTxs) {
            if (tx.points_change > 0) {
              const teacherRollbackRes = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${session?.access_token}`,
                },
                body: JSON.stringify({
                  action: 'deduct_points',
                  user_id: tx.user_id,
                  role: 'teacher',
                  points: tx.points_change,
                  point_type: 'activity_reward',
                  source_type: 'refund',
                  source_id: selectedOrder.id,
                  source_detail: { order_no: selectedOrder.orderNo, original_rebate: tx.points_change },
                  remark: `学员退款回滚教师返利: 订单${selectedOrder.orderNo}, 回滚${tx.points_change}U币`,
                }),
              });
              const teacherRollbackData = await teacherRollbackRes.json();
              if (!teacherRollbackData.success) {
                console.error('教师返利回滚失败:', teacherRollbackData.error);
              }
            }
          }
        }
      } catch (e) {
        console.error('查询教师返利记录失败:', e);
      }

      // 3. 处理支付宝退款
      const needAlipayRefund = refundForm.destination === 'original' &&
        (selectedOrder.payMethod === 'alipay' || selectedOrder.payMethod === 'points_alipay');

      if (needAlipayRefund) {
        const alipayRefundAmount = selectedOrder.payMethod === 'points_alipay'
          ? (Number(selectedOrder.actualPrice) - (selectedOrder.deductionAmount || 0))
          : refundForm.amount;

        if (alipayRefundAmount > 0) {
          try {
            const refundRequestBody = {
                action: 'createRefund',
                orderId: selectedOrder.id,
                refundAmount: alipayRefundAmount,
                refundReason: refundForm.reason || '后台退款',
              };
            const refundRes = await fetch(`${getSupabaseUrl()}/functions/v1/alipay-service`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session?.access_token}`,
              },
              body: JSON.stringify(refundRequestBody),
            });
            const refundData = await refundRes.json();
            await supabase.from('alipay_service_logs').insert({
              action: 'frontend_createRefund',
              request_params: refundRequestBody,
              response_data: refundData,
              status_code: refundRes.status,
            });
            if (!refundData.success) {
              const errorMsg = [refundData.error, refundData.detail, refundData.subMsg, refundData.subCode].filter(Boolean).join(' - ');
              alert('支付宝退款失败: ' + (errorMsg || '未知错误'));
              return;
            }
          } catch (e) {
            alert('退款接口调用失败');
            return;
          }
        }
      }
    }

    const { error } = await supabase.from('orders').update({
      status: 'refunded',
      refund_status: 'refunded',
      refund_amount: refundForm.amount,
      refund_destination: refundForm.destination,
      refund_reason: refundForm.reason,
      refunded_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }).eq('id', selectedOrder.id).eq('role', 'student');

    if (error) return;

    // 退款后禁用对应的 user_packages
    try {
      await supabase
        .from('user_packages')
        .update({
          is_active: false,
          closed_by: 'refund',
          closed_at: new Date().toISOString(),
          closed_reason: `订单退款: ${selectedOrder.orderNo}`,
          updated_at: new Date().toISOString()
        })
        .eq('order_id', selectedOrder.id);
    } catch (e) {
      console.error('禁用套餐失败:', e);
    }

    // 发送退款通知
    try {
      const refundContent = isNoPaymentOrder
        ? `您的订单【${selectedOrder.orderNo}】已关闭，套餐已停用。如有疑问请联系客服。`
        : `您的订单【${selectedOrder.orderNo}】已成功退款。${selectedOrder.pointsDeducted && selectedOrder.pointsDeducted > 0 ? `已退回${selectedOrder.pointsDeducted}U币至您的账户。` : ''}${refundForm.amount > 0 ? `退款金额：¥${refundForm.amount.toFixed(2)}，退款去向：${refundForm.destination === 'original' ? '原路退回' : '平台余额'}。` : ''}如有疑问请联系客服。`;
      await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
          body: JSON.stringify({
            action: 'messages_send_notification',
            userIds: [selectedOrder.userId],
            title: isNoPaymentOrder ? '套餐关闭通知' : '订单退款通知',
            content: refundContent,
          }),
      });
    } catch (e) {
      // ignore
    }

    setShowRefundModal(false);
    setSelectedOrder(null);
    loadStats();

    // 触发订单更新事件，通知 OrderList 组件刷新列表
    window.dispatchEvent(new Event('order-updated'));
  };

  // 删除操作
  const openDeleteConfirm = (order: any) => {
    setSelectedOrder(order);
    setShowDeleteConfirm(true);
  };

  const handleDeleteOrder = async () => {
    if (!selectedOrder) return;
    await supabase.from('orders').delete().eq('id', selectedOrder.id).eq('role', 'student');
    setShowDeleteConfirm(false);
    setSelectedOrder(null);
    loadStats();

    // 触发订单更新事件，通知 OrderList 组件刷新列表
    window.dispatchEvent(new Event('order-updated'));
  };

  // 批量删除
  const handleBatchDeleteWithConfirm = (ids: string[]) => {
    setPendingBatchIds(ids);
    setShowBatchDeleteConfirm(true);
  };

  const handleBatchDelete = async () => {
    if (pendingBatchIds.length === 0) return;
    await supabase.from('orders').delete().in('id', pendingBatchIds).eq('role', 'student');
    setShowBatchDeleteConfirm(false);
    setPendingBatchIds([]);
    loadStats();

    // 触发订单更新事件，通知 OrderList 组件刷新列表
    window.dispatchEvent(new Event('order-updated'));
  };

  // 查看退款详情
  const openRefundDetail = (order: any) => {
    setSelectedOrder(order);
    setShowRefundDetailModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">学员订单</h1>
          <p className="text-gray-500 mt-1">查看学员购买套餐的订单记录</p>
        </div>
        <button onClick={fetchServerIp} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border">
          获取服务器IP
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="text-sm text-gray-500">总订单数</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="text-sm text-gray-500">待支付</div>
          <div className="text-2xl font-bold text-yellow-600 mt-1">{stats.pending}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="text-sm text-gray-500">已支付</div>
          <div className="text-2xl font-bold text-green-600 mt-1">{stats.paid}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="text-sm text-gray-500">已退款</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{stats.refunded}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="text-sm text-gray-500">总收入</div>
          <div className="text-2xl font-bold text-purple-600 mt-1">¥{stats.revenue.toFixed(2)}</div>
        </div>
      </div>

      {/* 搜索筛选 */}
      <div className="bg-white rounded-t-xl border-x border-t border-gray-200 p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="搜索订单号、学员姓名、手机号..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg outline-none">
            <option value="all">全部状态</option>
            <option value="pending">待支付</option>
            <option value="paid">已支付</option>
            <option value="expired">已关闭</option>
            <option value="refunded">已退款</option>
          </select>
          <select value={durationFilter} onChange={(e) => setDurationFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg outline-none">
            <option value="all">全部套餐</option>
            <option value="month">月包</option>
            <option value="quarter">季包</option>
            <option value="halfYear">半年包</option>
            <option value="year">年包</option>
            <option value="custom">自定义包</option>
          </select>
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg outline-none">
            <option value="all">全部渠道</option>
            <option value="self">自购</option>
            <option value="admin">后台</option>
          </select>
        </div>
      </div>

      {/* 订单列表 */}
      <OrderList
        type="admin-all-student"
        mode="page"
        filterExt={filterExt}
        showActions
        showBatchSelect
        onBatchDelete={handleBatchDeleteWithConfirm}
        onRefund={openRefundModal}
        onDelete={openDeleteConfirm}
        onViewRefundDetail={openRefundDetail}
      />

      {/* 退款弹窗 */}
      <AnimatePresence>
        {showRefundModal && selectedOrder && (() => {
          const isNoPayment = selectedOrder.payMethod === 'admin' || selectedOrder.payMethod === 'coupon';
          return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><RotateCcw className="w-5 h-5 text-orange-500" />{isNoPayment ? '关闭套餐' : '订单退款'}</h3>
                <button onClick={() => setShowRefundModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} className="text-gray-500" /></button>
              </div>
              <div className="p-4 space-y-4">
                {isNoPayment && (
                  <div className="bg-yellow-50 rounded-lg p-3 text-sm text-yellow-700">
                    此订单为{selectedOrder.payMethod === 'admin' ? '后台开通' : '券码兑换'}，无实际支付，退款将仅关闭套餐。
                  </div>
                )}
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">订单号</span><span className="font-mono">{selectedOrder.orderNo}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">学员</span><span>{selectedOrder.userName}</span></div>
                  {!isNoPayment && (
                    <>
                      <div className="border-t border-gray-200 my-2"></div>
                      <div className="flex justify-between text-sm"><span className="text-gray-500">订单总额</span><span>¥{Number(selectedOrder.originalPrice || 0).toFixed(2)}</span></div>
                      {selectedOrder.pointsDeducted && selectedOrder.pointsDeducted > 0 && (
                        <div className="flex justify-between text-sm text-orange-600">
                          <span>支出</span>
                          <span>-{selectedOrder.pointsDeducted}分 (约¥{(selectedOrder.deductionAmount || 0).toFixed(2)})</span>
                        </div>
                      )}
                      <div className="border-t border-gray-200 my-2"></div>
                      <div className="flex justify-between text-sm font-semibold">
                        <span className="text-gray-700">实付金额</span>
                        <span className="text-lg font-bold text-blue-600">¥{Number(selectedOrder.actualPrice ?? selectedOrder.originalPrice - (selectedOrder.deductionAmount || 0)).toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>
                {!isNoPayment && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">退款金额</label>
                    <input type="number" value={refundForm.amount} onChange={e => setRefundForm({...refundForm, amount: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 border rounded-lg" min={0} max={selectedOrder.actualPrice ?? selectedOrder.originalPrice} />
                  </div>
                )}
                {!isNoPayment && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">退款去向</label>
                    <select value={refundForm.destination} onChange={e => setRefundForm({...refundForm, destination: e.target.value as 'original' | 'balance'})} className="w-full px-3 py-2 border rounded-lg">
                      <option value="original">原路退回</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{isNoPayment ? '关闭原因' : '退款说明'}</label>
                  <textarea value={refundForm.reason} onChange={e => setRefundForm({...refundForm, reason: e.target.value})} placeholder={isNoPayment ? '请输入关闭原因...' : '请输入退款原因...'} rows={3} className="w-full px-3 py-2 border rounded-lg resize-none" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowRefundModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">取消</button>
                  <button onClick={handleRefundSubmit} disabled={!refundForm.reason.trim() || (!isNoPayment && refundForm.amount <= 0 && !(selectedOrder.pointsDeducted && selectedOrder.pointsDeducted > 0))} className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50">{isNoPayment ? '确认关闭' : '确认退款'}</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* 删除确认弹窗 */}
      <AnimatePresence>
        {showDeleteConfirm && selectedOrder && (
          <AuthConfirmModal
            isOpen={showDeleteConfirm}
            title="确认删除订单"
            message={`确定要删除订单"${selectedOrder.orderNo}"吗？删除后该订单将从全网所有位置彻底删除，包括学员中心、学员详情等所有展示位置。此操作不可恢复。`}
            onConfirm={handleDeleteOrder}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        )}
      </AnimatePresence>

      {/* 退款详情弹窗 */}
      <AnimatePresence>
        {showRefundDetailModal && selectedOrder && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowRefundDetailModal(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Eye className="w-5 h-5 text-blue-500" />退款详情</h3>
                <button onClick={() => setShowRefundDetailModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">订单号</span><span className="font-mono">{selectedOrder.orderNo}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">学员</span><span>{selectedOrder.userName}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">实付金额</span><span className="text-gray-800">¥{Number(selectedOrder.actualPrice || 0).toFixed(2)}</span></div>
                </div>
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center"><CheckCircle className="w-4 h-4 text-green-600" /></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">退款申请已处理</div>
                      <div className="text-xs text-gray-500">{formatDateTime(selectedOrder.updatedAt)}</div>
                    </div>
                  </div>
                  <div className="ml-4 pl-4 border-l-2 border-green-200 space-y-3">
                    <div className="flex justify-between text-sm"><span className="text-gray-500">退款金额</span><span className="font-bold text-red-600">¥{Number(selectedOrder.refundAmount || selectedOrder.actualPrice || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-500">退款去向</span><span className="text-gray-800">{selectedOrder.refundDestination === 'original' ? '原路退回' : '退回平台余额'}</span></div>
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center"><Clock className="w-4 h-4 text-blue-600" /></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">退款到账中</div>
                      <div className="text-xs text-gray-500">预计1-3个工作日到账</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 批量删除确认弹窗 */}
      <AnimatePresence>
        {showBatchDeleteConfirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-500" />确认批量删除</h3>
                <button onClick={() => setShowBatchDeleteConfirm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} className="text-gray-500" /></button>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-red-700 text-sm">删除后选中的 {pendingBatchIds.length} 条订单将从全网所有位置彻底删除，包括学员中心、学员详情等所有展示位置。此操作不可恢复。</p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowBatchDeleteConfirm(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">取消</button>
                  <button onClick={handleBatchDelete} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">确认删除</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
